import {
  ClientPartner,
  ClientPartnerPackage,
  ExternalSystemRow,
  GoLiveCheck,
  PartnerEndpointGroup,
  PartnerPackageOperation,
  PartnerPackageRecipient,
  PartnerPackageSettingChange,
  PartnerPackageSettingKey,
  PARTNER_ACCEPTS_FIELDS,
  PARTNER_CONTACT_ROLES,
  PARTNER_ENDPOINT_FIELDS,
  PARTNER_STATUSES,
  PartnerChangeGroup,
  PartnerChanges,
  PartnerContact,
  PartnerContactRole,
  PartnerSignIn,
} from "@/data/models/ClientPartner";
import { EMPTY_VALUE } from "@/utils/ClientAdministrationDetail";

/**
 * A partner's page in the IRES Admin Client (Track #2610084, merge 2): the fields each card edits, the
 * values its dialog starts from, and the change it sends. Pure. IRES Cloud repeats every rule
 * (`PartnerAdministrationFields.ts`); see `docs/admin/external-systems.md`.
 */

const PARTNERS = "admin.clients.partners";

/** One editable field of a card's dialog. */
export interface PartnerFieldSpec {
  /** The field as IRES Cloud names it (`endpoints.createProject` for an endpoint). */
  field: string;
  kind: "status" | "text" | "switch";
  labelKey: string;
  /** For an endpoint: the built-in name used while it is empty. */
  placeholder?: string;
}

/** The built-in endpoint names IRES Cloud uses when none is set (`ExternalApi`). */
export const DEFAULT_ENDPOINTS: Record<string, string> = {
  createProject: "createProject",
  projectData: "projectData",
  projectStatus: "projectStatus",
  noteUpdate: "note",
};

/** The fields of a card, in the order its dialog shows them; the test-system switch only where allowed. */
export function partnerFieldSpecs(group: PartnerChangeGroup, partner: ClientPartner): PartnerFieldSpec[] {
  switch (group) {
    case "PARTNER_STATUS":
      return [{ field: "integrationStatus", kind: "status", labelKey: `${PARTNERS}.fields.integrationStatus` }];
    case "PARTNER_CONNECTION":
      return [
        { field: "baseUrl", kind: "text", labelKey: `${PARTNERS}.fields.baseUrl` },
        ...PARTNER_ENDPOINT_FIELDS.map((field) => ({
          field: `endpoints.${field}`,
          kind: "text" as const,
          labelKey: `${PARTNERS}.endpoints.${field}`,
          placeholder: DEFAULT_ENDPOINTS[field],
        })),
      ];
    case "PARTNER_ACCEPTS":
      return PARTNER_ACCEPTS_FIELDS.map((field) => ({ field, kind: "switch" as const, labelKey: `${PARTNERS}.accepts.${field}` }));
    case "PARTNER_CONTACTS":
      // Edited in its own dialog (a list), not as single fields.
      return [];
    case "PARTNER_BEHAVIOUR":
      return [
        { field: "appointmentFieldSwap", kind: "switch", labelKey: `${PARTNERS}.behaviour.appointmentFieldSwap` },
        { field: "incomingContactPerson0", kind: "switch", labelKey: `${PARTNERS}.behaviour.incomingContactPerson0` },
        { field: "sendsLatestProjectStatus", kind: "switch", labelKey: `${PARTNERS}.behaviour.sendsLatestProjectStatus` },
        // Only with the owner's ERP-Simulator (D5); a partner already marked keeps the switch to turn it off.
        ...(partner.mayBeTestSystem || partner.behaviour.isTestSystem
          ? [{ field: "isTestSystem", kind: "switch" as const, labelKey: `${PARTNERS}.behaviour.isTestSystem` }]
          : []),
      ];
  }
}

/** A field's value as served, the form's starting point and the `expected` of a change. */
export function partnerFieldValue(partner: ClientPartner, field: string): unknown {
  if (field === "integrationStatus") {
    return partner.status;
  }
  if (field === "baseUrl") {
    return partner.connection.baseUrl;
  }
  if (field.startsWith("endpoints.")) {
    return partner.connection.endpoints[field.slice("endpoints.".length) as keyof ClientPartner["connection"]["endpoints"]];
  }
  if ((PARTNER_ACCEPTS_FIELDS as string[]).includes(field)) {
    return partner.accepts[field as keyof ClientPartner["accepts"]];
  }
  return partner.behaviour[field as keyof ClientPartner["behaviour"]];
}

/** The dialog's form for a card: every field's served value. */
export function partnerForm(group: PartnerChangeGroup, partner: ClientPartner): Record<string, unknown> {
  const form: Record<string, unknown> = {};
  for (const spec of partnerFieldSpecs(group, partner)) {
    const value = partnerFieldValue(partner, spec.field);
    form[spec.field] = spec.kind === "text" ? value ?? "" : value;
  }
  return form;
}

/** A text as IRES Cloud stores it: trimmed, empty as null. */
function normalisedText(value: unknown): string | null {
  return typeof value === "string" && value.trim() !== "" ? value.trim() : null;
}

/**
 * The change a card's dialog sends: each field whose value differs from what was served, with that
 * served value as `expected`. An unset switch (null) that was not touched stays out.
 */
export function partnerChanges(group: PartnerChangeGroup, partner: ClientPartner, form: Record<string, unknown>): PartnerChanges {
  const changes: PartnerChanges = {};
  for (const spec of partnerFieldSpecs(group, partner)) {
    const expected = partnerFieldValue(partner, spec.field);
    const value = spec.kind === "text" ? normalisedText(form[spec.field]) : form[spec.field];
    if (value !== expected && !(value === null && expected === null)) {
      changes[spec.field] = { expected, value };
    }
  }
  return changes;
}

/** Whether the address is one IRES Cloud accepts: empty, or an http(s) address. */
export function isValidPartnerAddress(text: string): boolean {
  if (text.trim() === "") {
    return true;
  }
  try {
    const url = new URL(text.trim());
    return url.protocol === "https:" || url.protocol === "http:";
  } catch {
    return false;
  }
}

/** Whether an endpoint is one IRES Cloud accepts: empty, or a path of letters, digits and `_-.~/`. */
export function isValidPartnerEndpoint(text: string): boolean {
  return text.trim() === "" || /^[A-Za-z0-9_\-.~/]{1,100}$/.test(text.trim().replace(/^\/+/, ""));
}

/** The credentials the dialog may replace, per way of signing in (IRES Cloud's `CREDENTIAL_FIELDS_BY_SIGN_IN`). */
export const CREDENTIAL_FIELDS: Record<PartnerSignIn, string[]> = {
  API_KEY: ["apiKey"],
  SIGN_IN: ["email", "password", "apiKey", "authUrl", "tokenUrl"],
  EASY_TEC: ["clientId", "password"],
};

/** The secrets among them: entered hidden, shown only as set or missing. */
export const SECRET_CREDENTIAL_FIELDS = ["apiKey", "password"];

/** The statuses in the order the dialog offers them. */
export const PARTNER_STATUS_ORDER = PARTNER_STATUSES;

/** The label of how the partner gets steps: by `system`. */
export function partnerSystemLabelKey(system: string | null): string {
  return system === "EASY_TEC" || system === "EXTERNAL" ? `${PARTNERS}.systems.${system}` : `${PARTNERS}.systems.NONE`;
}

/** The i18n key for IRES Cloud's refusal (`details.reason`), or the general failure. */
export function partnerErrorKey(error: unknown): string {
  const reason = (error as { details?: { reason?: unknown } } | null)?.details?.reason;
  const known = [
    "CHANGED_MEANWHILE", "NOT_EDITABLE_HERE", "NEEDS_ERP_SIMULATOR", "NO_PARTNER", "INVALID", "SHARED_LOGIN", "EMAIL_TAKEN",
    "MEMBER_CLIENT", "NOT_A_MEMBER", "NO_CHANGES",
    // Integration packages (#2610090).
    "NOT_AVAILABLE", "NO_PACKAGE",
  ];
  return typeof reason === "string" && known.includes(reason) ? `${PARTNERS}.errors.${reason}` : `${PARTNERS}.errors.FAILED`;
}

/**
 * The sign-in address the create dialog proposes for a new external system (Taimoor, 2026-10-23): the
 * client's Kürzel and the system's name, `aqua-x360@ires.de`, lower case with umlauts written out and
 * anything else a hyphen. Empty until there is a name.
 */
export function proposedPartnerEmail(clientPrefix: string | null, name: string): string {
  const slug = (text: string) =>
    text
      .toLowerCase()
      .replace(/ä/g, "ae")
      .replace(/ö/g, "oe")
      .replace(/ü/g, "ue")
      .replace(/ß/g, "ss")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
  const namePart = slug(name);
  if (namePart === "") {
    return "";
  }
  const prefixPart = slug(clientPrefix ?? "");
  return `${prefixPart ? `${prefixPart}-` : ""}${namePart}@ires.de`;
}

/** Whether a text is a sign-in address, as IRES Cloud checks it. */
export function isPartnerEmail(text: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(text.trim());
}

/** The "Externe Systeme" list's filter: every system, or one status. */
export type ExternalSystemFilter = "ALL" | "SETUP" | "TEST" | "LIVE" | "PAUSED";

export const EXTERNAL_SYSTEM_FILTERS: ExternalSystemFilter[] = ["ALL", "LIVE", "TEST", "SETUP", "PAUSED"];

/** The list's rows under its filter and search: name, owner or a linked client, case-insensitive. */
export function filterExternalSystems(rows: ExternalSystemRow[], filter: ExternalSystemFilter, search: string): ExternalSystemRow[] {
  const query = search.trim().toLowerCase();
  return rows.filter(
    (row) =>
      (filter === "ALL" || row.status === filter) &&
      (query === "" || [row.name, row.ownerName, ...row.linkedClientNames].some((text) => text.toLowerCase().includes(query)))
  );
}

/** A contact person as the dialog edits it: texts as typed, "" for nothing. */
export interface PartnerContactForm {
  name: string;
  role: PartnerContactRole;
  email: string;
  phone: string;
}

const CONTACT_PHONE_PATTERN = /^[0-9+()/\-. ]{3,40}$/;

/** The dialog's rows for the served contacts. */
export function partnerContactForms(contacts: PartnerContact[] | undefined): PartnerContactForm[] {
  return (contacts ?? []).map((contact) => ({ name: contact.name, role: contact.role, email: contact.email ?? "", phone: contact.phone ?? "" }));
}

/** A row as IRES Cloud stores it: trimmed, the address in lower case, an empty text as null. */
export function partnerContactFromForm(form: PartnerContactForm): PartnerContact {
  const email = form.email.trim().toLowerCase();
  const phone = form.phone.trim();
  return { name: form.name.trim(), role: form.role, email: email === "" ? null : email, phone: phone === "" ? null : phone };
}

/** What is wrong with a row, as IRES Cloud would refuse it; none when it can be saved. */
export function partnerContactErrors(form: PartnerContactForm): Array<"name" | "role" | "email" | "phone"> {
  const errors: Array<"name" | "role" | "email" | "phone"> = [];
  const contact = partnerContactFromForm(form);
  if (contact.name === "" || contact.name.length > 100) {
    errors.push("name");
  }
  if (!PARTNER_CONTACT_ROLES.includes(contact.role)) {
    errors.push("role");
  }
  if (contact.email !== null && (!isPartnerEmail(contact.email) || contact.email.length > 100)) {
    errors.push("email");
  }
  if (contact.phone !== null && !CONTACT_PHONE_PATTERN.test(contact.phone)) {
    errors.push("phone");
  }
  return errors;
}

/**
 * The contact change the dialog sends: the whole list as IRES Cloud stores it, with the served list as
 * `expected`; empty when nothing differs.
 */
export function partnerContactChanges(served: PartnerContact[] | undefined, forms: PartnerContactForm[]): PartnerChanges {
  const expected = served ?? [];
  const value = forms.map(partnerContactFromForm);
  return JSON.stringify(value) === JSON.stringify(expected) ? {} : { contacts: { expected, value } };
}

/** The go-live items not done yet, in the checklist's order. */
export function openGoLiveChecks(checks: GoLiveCheck[] | undefined): GoLiveCheck[] {
  return (checks ?? []).filter((check) => !check.done);
}

/** The recipient choice in the package dialog: a contact person by its index, or someone typed. */
export type PackageRecipientChoice = number | "OTHER";

/**
 * Whom a package is for (#2610090): the chosen contact person (name and address as recorded), or the
 * typed name and optional address; null while not valid.
 */
export function packageRecipient(
  choice: PackageRecipientChoice | null,
  contacts: PartnerContact[] | undefined,
  typed: { name: string; email: string }
): PartnerPackageRecipient | null {
  if (typeof choice === "number") {
    const contact = (contacts ?? [])[choice];
    return contact ? { name: contact.name, email: contact.email } : null;
  }
  if (choice !== "OTHER") {
    return null;
  }
  const name = typed.name.trim();
  const email = typed.email.trim().toLowerCase();
  if (name === "" || name.length > 100 || (email !== "" && (!isPartnerEmail(email) || email.length > 100))) {
    return null;
  }
  return { name, email: email === "" ? null : email };
}

/** The ticked groups in the dialog's order, the sign-in always among them (IRES Cloud adds it too). */
export function packageGroups(offered: PartnerEndpointGroup[], ticked: PartnerEndpointGroup[]): PartnerEndpointGroup[] {
  return offered.filter((group) => group === "SIGN_IN" || ticked.includes(group));
}

/** A recipient as one line: "Max Muster <max@partner.de>", the name alone without an address. */
export function packageRecipientText(recipient: PartnerPackageRecipient): string {
  return recipient.email ? `${recipient.name} <${recipient.email}>` : recipient.name;
}

/** The i18n key naming a setting an overview states, reusing the system page's own labels. */
const PACKAGE_SETTING_LABEL_KEYS: Record<PartnerPackageSettingKey, string> = {
  name: "admin.clients.partners.packages.settingNames.name",
  accessEmail: "admin.clients.partners.packages.settingNames.accessEmail",
  ownerIsGroup: "admin.clients.partners.packages.settingNames.ownerIsGroup",
  sends: "admin.clients.partners.packages.settingNames.sends",
  baseUrl: "admin.clients.partners.fields.baseUrl",
  signIn: "admin.clients.partners.fields.signIn",
  newProjects: "admin.clients.partners.accepts.acceptsNewProjects",
  projectData: "admin.clients.partners.accepts.acceptsProjectDataUpdates",
  projectStatus: "admin.clients.partners.accepts.acceptsProjectStatusUpdates",
  notes: "admin.clients.partners.accepts.acceptsNotes",
  includeProjectOwnersInfo: "admin.clients.partners.accepts.includeProjectOwnersInfo",
  sendsExtraPhaseData: "admin.clients.partners.accepts.sendsExtraPhaseData",
  appointmentFieldSwap: "admin.clients.partners.behaviour.appointmentFieldSwap",
  incomingContactPerson0: "admin.clients.partners.behaviour.incomingContactPerson0",
  sendsLatestProjectStatus: "admin.clients.partners.behaviour.sendsLatestProjectStatus",
  isTestSystem: "admin.clients.partners.behaviour.isTestSystem",
};

/** What IRES sends: the address, or null for "not sent". */
const PACKAGE_SEND_TARGET_KEYS: PartnerPackageSettingKey[] = ["newProjects", "projectData", "projectStatus", "notes"];

/**
 * One changed setting of a package as a line (#2610090 merge 2): "Notizen: nicht gesendet → https://…".
 *
 * @param translate - `$t` of the calling component, as text.
 */
export function packageSettingChangeText(change: PartnerPackageSettingChange, translate: (key: string) => string): string {
  const valueText = (value: string | boolean | null): string => {
    if (typeof value === "boolean") {
      return translate(value ? "common.yes" : "common.no");
    }
    if (value === null) {
      return PACKAGE_SEND_TARGET_KEYS.includes(change.key) ? translate("admin.clients.partners.packages.notSent") : EMPTY_VALUE;
    }
    return change.key === "signIn" ? translate(`admin.clients.partners.signIn.${value}`) : value;
  };
  return `${translate(PACKAGE_SETTING_LABEL_KEYS[change.key])}: ${valueText(change.before)} → ${valueText(change.after)}`;
}

/** Endpoints as one line, "GET /projectStatus, PUT /projectStatus". */
export function packageOperationsText(operations: PartnerPackageOperation[]): string {
  return operations.map((operation) => `${operation.method} ${operation.path}`).join(", ");
}

/** The list after a new package: it goes on top, and every earlier package is superseded by it. */
export function packagesAfterCreating(created: ClientPartnerPackage, packages: ClientPartnerPackage[]): ClientPartnerPackage[] {
  return [created, ...packages.map((item): ClientPartnerPackage => ({ ...item, state: "SUPERSEDED", outdated: undefined }))];
}
